import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { ChipModule } from 'primeng/chip';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { DatasetService } from '../../services/dataset.service';
import { AuthService } from '../../services/auth.service';
import { Dataset, DatasetStats } from '../../models/dataset.model';

@Component({
  selector: 'app-datasets',
  standalone: true,
  imports: [
    CommonModule,
    ButtonModule,
    CardModule,
    ChipModule,
    ProgressSpinnerModule
  ],
  template: `
    <div class="min-h-screen bg-gray-50">
      <!-- Header -->
      <header class="bg-white shadow-sm border-b">
        <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div class="flex justify-between items-center py-4">
            <h1 class="text-xl font-semibold text-gray-900">Available Datasets</h1>
            <div class="flex items-center space-x-4">
              <span class="text-sm text-gray-600">
                Welcome, {{ currentUser?.firstName }} {{ currentUser?.lastName }}
              </span>
              <p-button
                label="Go to Chat"
                severity="primary"
                size="small"
                (onClick)="goToChat()"
              ></p-button>
              <p-button
                label="Logout"
                severity="secondary"
                size="small"
                (onClick)="logout()"
              ></p-button>
            </div>
          </div>
        </div>
      </header>

      <!-- Main Content -->
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div *ngIf="isLoading" class="flex justify-center py-8">
          <p-progressSpinner strokeWidth="4" animationDuration=".5s"></p-progressSpinner>
        </div>

        <div *ngIf="!isLoading && datasets.length === 0" class="text-center py-8">
          <p class="text-gray-500">No datasets available.</p>
        </div>

        <div *ngIf="!isLoading && datasets.length > 0" class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <p-card 
            *ngFor="let dataset of datasets"
            [header]="dataset.name"
            styleClass="h-full cursor-pointer hover:shadow-lg transition-shadow"
            (click)="selectDataset(dataset)"
          >
            <div class="space-y-4">
              <div>
                <p class="text-gray-600 text-sm">{{ dataset.description }}</p>
              </div>
              
              <div class="flex items-center justify-between">
                <div class="flex items-center space-x-2">
                  <span class="text-sm text-gray-500">Documents:</span>
                  <p-chip [label]="dataset.documentCount.toString()"></p-chip>
                </div>
                <p-chip 
                  [label]="dataset.isActive ? 'Active' : 'Inactive'"
                  [class]="dataset.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'"
                ></p-chip>
              </div>

              <div *ngIf="datasetStats[dataset.id]" class="space-y-2">
                <div class="text-xs text-gray-500">Statistics:</div>
                <div class="grid grid-cols-2 gap-2 text-xs">
                  <div>
                    <span class="text-gray-500">Conversations:</span>
                    <span class="ml-1 font-medium">{{ datasetStats[dataset.id].totalConversations }}</span>
                  </div>
                  <div>
                    <span class="text-gray-500">Questions:</span>
                    <span class="ml-1 font-medium">{{ datasetStats[dataset.id].totalQuestions }}</span>
                  </div>
                  <div>
                    <span class="text-gray-500">Avg Confidence:</span>
                    <span class="ml-1 font-medium">{{ (datasetStats[dataset.id].averageConfidence * 100).toFixed(1) }}%</span>
                  </div>
                  <div>
                    <span class="text-gray-500">Avg Response:</span>
                    <span class="ml-1 font-medium">{{ datasetStats[dataset.id].averageResponseTime }}ms</span>
                  </div>
                </div>
              </div>

              <div class="text-xs text-gray-400">
                Created: {{ dataset.createdAt | date:'short' }}
              </div>
            </div>
          </p-card>
        </div>
      </div>
    </div>
  `,
  styles: []
})
export class DatasetsComponent implements OnInit {
  datasets: Dataset[] = [];
  datasetStats: { [key: string]: DatasetStats } = {};
  isLoading = false;
  currentUser: any;

  constructor(
    private datasetService: DatasetService,
    private authService: AuthService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.currentUser = this.authService.getCurrentUser();
    this.loadDatasets();
  }

  loadDatasets(): void {
    this.isLoading = true;
    this.datasetService.getDatasets().subscribe({
      next: (datasets) => {
        this.datasets = datasets;
        this.loadDatasetStats();
        this.isLoading = false;
      },
      error: (error) => {
        console.error('Error loading datasets:', error);
        this.isLoading = false;
      }
    });
  }

  loadDatasetStats(): void {
    this.datasets.forEach(dataset => {
      this.datasetService.getDatasetStats(dataset.id).subscribe({
        next: (stats) => {
          this.datasetStats[dataset.id] = stats;
        },
        error: (error) => {
          console.error(`Error loading stats for dataset ${dataset.id}:`, error);
        }
      });
    });
  }

  selectDataset(dataset: Dataset): void {
    // Store selected dataset and navigate to chat
    localStorage.setItem('selectedDataset', JSON.stringify(dataset));
    this.router.navigate(['/chat']);
  }

  goToChat(): void {
    this.router.navigate(['/chat']);
  }

  logout(): void {
    this.authService.logout();
    // Redirect will be handled by auth guard
  }
} 